import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { filter, Subscription } from 'rxjs';
import { Question } from 'src/app/core/models/questionnaire.model';
import { DynamicService } from 'src/app/core/services/base/dynamic.service';
import { UserService } from 'src/app/core/services/user.service';
import { AutofillFilter } from '../question-text/question-text.component';
import { QuestionAnswer } from '../question/question.component';

@Component({
  selector: 'app-question-phone',
  templateUrl: './question-phone.component.html',
  styleUrls: ['./question-phone.component.scss'],
})
export class QuestionPhoneComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input('question') question!: Question;
  @Input() user: any;
  @Output('changeAnswer') changeAnswer: EventEmitter<QuestionAnswer> =
    new EventEmitter();

  phone = new FormGroup({ number: new FormControl('', Validators.required) });

  sub$!: Subscription;
  preferredCountries: string[] = [];

  autofillMode: boolean = false;
  autofillEditable: boolean = false;
  userId: string = '';
  isValid: boolean = true;
  private internalValue: string = '';
  inputMode: string = 'numeric';
  autocomplete: string | null = null;
  private readonly validInputModes = ['none', 'text', 'decimal', 'numeric', 'tel', 'search', 'email', 'url'];

  constructor(
    private dynamicService: DynamicService<any>,
    private userService: UserService,
    private el: ElementRef,
    private zone: NgZone
  ) {}

  async ngOnInit() {
    const configInputMode = this.question.configuration?.inputmode;
    if (configInputMode && this.validInputModes.includes(configInputMode)) {
      this.inputMode = configInputMode;
    }
    const configAutocomplete = this.question.configuration?.autocomplete;
    if (configAutocomplete) {
      this.autocomplete = configAutocomplete;
    }

    // Configurar países preferidos primeiro
    if (this.question.configuration && this.question.configuration.prefer) {
      this.preferredCountries = this.question.configuration.prefer;
    } else {
      this.preferredCountries = ['gb', 'us']; // fallback padrão
    }

    if (this.question.configuration.autofill && !this.question.answer?.value) {
      await this.processAutofill(this.question.configuration.autofill);
      this.change();
    }
    if (this.question.answer && this.question.answer.value) {
      this.internalValue = this.question.answer.value.toString();
      this.phone.controls.number.setValue(
        this.question.answer.value.toString()
      );
      this.change();
    }

    this.sub$ = this.phone.controls.number.valueChanges
      .pipe(filter((value) => value !== null && value !== undefined))
      .subscribe((value) => {
        this.internalValue = value || '';
        this.isValid = true;
        this.change();
      });
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      const btn = (this.el.nativeElement as HTMLElement).querySelector<HTMLElement>('button.country-selector');
      if (!btn) return;
      const isTouchDevice = 'ontouchstart' in window;
      btn.addEventListener('click', () => {
        if (!isTouchDevice) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const panel = document.querySelector<HTMLElement>('.ngx-mat-tel-input-mat-menu-panel');
              if (!panel) return;
              const pane = panel.closest<HTMLElement>('.cdk-overlay-pane');
              if (pane) {
                const rect = btn.getBoundingClientRect();
                pane.style.top = `${rect.bottom + window.scrollY}px`;
                pane.style.bottom = 'auto';
              }
            });
          });
        }
        setTimeout(() => {
          const focusSearch = () => {
            const panel = document.querySelector<HTMLElement>('.ngx-mat-tel-input-mat-menu-panel');
            if (!panel) {
              document.removeEventListener('focusout', onFocusOut, true);
              return;
            }
            const input = panel.querySelector<HTMLElement>('input');
            if (input && !input.hasAttribute('data-ks-bound')) {
              input.setAttribute('data-ks-bound', '1');
              input.addEventListener('keydown', (e) => e.stopPropagation());
            }
            input?.focus();
          };

          const onFocusOut = () => {
            requestAnimationFrame(() => {
              if (document.activeElement === document.body || document.activeElement === null) {
                focusSearch();
              }
            });
          };

          document.addEventListener('focusout', onFocusOut, true);
          focusSearch();
        }, 300);
      });
    });
  }

  ngOnDestroy(): void {
    if (this.sub$) {
      this.sub$.unsubscribe();
    }
  }
  private async processAutofill(autofillConfig: any) {
    this.autofillMode = true;
    this.autofillEditable = autofillConfig.editable ?? false;
    await this.fetchCurrentUserId();

    const filters = this.replacePlaceholdersInFilters(autofillConfig.filters);
    const autofillValue = await this.fetchAutofillValue(
      autofillConfig.from,
      autofillConfig.select,
      filters
    );

    if (autofillValue) {
      this.question.answer = { value: autofillValue };
      this.phone.controls.number.setValue(autofillValue);
      this.internalValue = autofillValue;

      this.change();
    }
  }

  private async fetchCurrentUserId() {
    if (this.user) {
      this.userId = this.user.id;
      return;
    }
    const currentUser = await this.userService.getCurrentUser();
    this.userId = currentUser?.id || '';
  }

  private replacePlaceholdersInFilters(
    filters: AutofillFilter[]
  ): AutofillFilter[] {
    return filters.map((filter) => ({
      ...filter,
      val: filter.val.replace('$userId', this.userId),
    }));
  }

  private async fetchAutofillValue(
    from: string,
    select: string,
    filters: AutofillFilter[]
  ): Promise<string | null> {
    try {
      this.dynamicService.load(from);
      const result = await this.dynamicService.autofillQuery(
        from,
        select,
        filters
      );

      return (
        result?.[0]?.[
          (select as any).split('->>')[(select as any).split('->>').length - 1]
        ] || null
      );
    } catch (error) {
      console.error('Erro ao buscar valor de autofill:', error);
      return null;
    }
  }

  change() {
    this.question.answer!.value = this.internalValue;
    this.question.answer!.addtional_information = {
      phone: this.internalValue,
    };
    this.question.isValid = this.isValid;
    this.changeAnswer.next({
      answer: this.question.answer!,
      question: this.question,
    });
  }

  internalChange(event: any) {
    if (this.phone.controls.number.value == null) {
      this.internalValue = event.target.value;
      this.isValid = false;
      this.change();
    }
  }
}
